import { NIVEL } from "./niveles-de-permiso";

/**
 * OpenSpec 028 — la unidad en que el operador lee el margen.
 *
 * `quote_access_margins` dejó de ser un boolean y pasó a ser la unidad:
 *
 *   - `none`     → no ve la columna de margen ni la fila del resumen
 *   - `markup`   → las ve, sobre el **costo** (el comportamiento histórico)
 *   - `markdown` → las ve, sobre el **precio de venta**
 *
 * Es la misma ganancia con otro denominador, y es unidad de **display**: no
 * cambia nada de lo que se persiste.
 *
 * Este módulo existe porque el mapeo legacy se necesita en **cuatro** paneles de
 * permisos distintos (store, store→linked seller, seller, seller→linked store).
 * Repetirlo en cada uno garantiza que en algún momento uno quede desactualizado
 * y muestre el permiso apagado para un store que sí ve márgenes.
 */
export const MARGIN_UNITS = ["none", "markup", "markdown"] as const;

export type MarginUnit = (typeof MARGIN_UNITS)[number];

/**
 * Resuelve el valor crudo del config a una unidad, mapeando los valores legacy.
 * Espeja `normalizeMarginUnit` del API (`quotes/constants/quote.constants.ts`).
 *
 * `'true'` → `markup` es lo que hace que un store que hoy ve márgenes siga
 * viéndolos, y que el control del panel aparezca en **Markup** en vez de vacío.
 * Sin esto, un operador que abre el panel de un store legacy ve "No access" y,
 * si guarda, se saca el permiso a sí mismo sin haber tocado nada.
 *
 * Fail-closed: cualquier cosa que no se reconozca es `none`.
 */
export const normalizeMarginUnit = (
  raw: string | null | undefined,
): MarginUnit => {
  if (raw === "markup" || raw === "markdown") return raw;
  if (raw === "true") return "markup";
  return "none";
};

/**
 * ¿El operador ve los datos de margen?
 *
 * **Nunca gatear por truthiness sobre el valor del config**: `'none'` es un
 * string no vacío, así que `!!'none'` es `true` y la columna se abriría para
 * quien no tiene acceso.
 *
 * (En el payload del presupuesto el API manda `null` en vez de `'none'`
 * justamente para que la truthiness siga siendo segura del lado del que lee
 * `seller.marginAccess` — pero acá, en los paneles, el valor viene del config
 * crudo y hay que comparar.)
 */
export const hasMarginAccess = (raw: string | null | undefined): boolean =>
  normalizeMarginUnit(raw) !== "none";

/** ¿En qué unidad? `null` cuando no hay acceso. */
export const marginUnitOf = (
  raw: string | null | undefined,
): Exclude<MarginUnit, "none"> | null => {
  const unit = normalizeMarginUnit(raw);
  return unit === "none" ? null : unit;
};

/**
 * Etiquetas de la unidad, para las opciones del select de permisos.
 *
 * Sin i18n a propósito: "Markup" y "Markdown" son los términos que usa el
 * negocio en las dos lenguas del panel, igual que "SKU" o "IVA". Traducirlos
 * ("Margen sobre costo") perdería el término que el cliente pidió por nombre.
 */
export const MARGIN_UNIT_OPTIONS: { label: string; value: MarginUnit }[] = [
  { label: NIVEL.no_access, value: "none" },
  { label: "Markup (sobre el costo)", value: "markup" },
  { label: "Markdown (sobre la venta)", value: "markdown" },
];

/**
 * Etiqueta de la unidad para los paneles de **solo lectura** (los del lado del
 * seller, donde el permiso se ve pero no se edita).
 *
 * Devuelve texto y no un control deshabilitado porque es lo que hacen los
 * campos de al lado en esos mismos paneles — la comisión, que también es un
 * enum de tres valores, se renderiza como `<Text>`.
 */
export const marginUnitLabel = (raw: string | null | undefined): string =>
  MARGIN_UNIT_OPTIONS.find((o) => o.value === normalizeMarginUnit(raw))?.label ??
  NIVEL.no_access;

/**
 * Los textos de la unidad en la pantalla del presupuesto.
 *
 * Tres sitios, y el tercero es el que se olvida:
 *
 *   1. `column`  — encabezado de la columna en la tabla de productos
 *   2. `summary` — fila del margen en el resumen, y la entrada del **selector de
 *                  columnas** (si esa quedara en "Margen", el operador prende
 *                  algo llamado "Margen" y aparece una columna llamada `mDn%`)
 *   3. `tooltip` — la fórmula, porque `mUp%` / `mDn%` son tersos y ya no dicen
 *                  "Margen"
 *
 * El `tooltip` incluye el hint de ordenar a propósito: se pinta como atributo
 * `title` nativo sobre el texto del label del encabezado, que ya trae uno
 * ("Ordenar por esta columna") — el del elemento más interno es el que el
 * browser muestra, así que si no lo repitiéramos, esta columna sería la única
 * que pierde el hint.
 *
 * Se usa el `title` nativo y **no** un `Tooltip` de antd: el `<th>` tiene dos
 * gestos vivos (arrastrar el grip reordena columnas, click en el label ordena
 * filas) y un atributo no puede interferir con ninguno de los dos, mientras que
 * un componente que clona el hijo y le agrega handlers sí podría.
 */
export const MARGIN_UNIT_COPY: Record<
  Exclude<MarginUnit, "none">,
  { column: string; summary: string; tooltip: string }
> = {
  markup: {
    column: "mUp%",
    summary: "Markup",
    tooltip:
      "Markup — margen sobre el costo: (venta − costo) / costo · Click para ordenar",
  },
  markdown: {
    column: "mDn%",
    summary: "Markdown",
    tooltip:
      "Markdown — margen sobre la venta: (venta − costo) / venta · Click para ordenar",
  },
};

/**
 * Los textos de la unidad activa, o `null` si el operador no ve márgenes.
 *
 * Acepta tanto el valor crudo del config (`'true'`, `'markup'`, …) como el que
 * viaja en el payload del presupuesto (`null | 'markup' | 'markdown'`): las dos
 * formas pasan por `normalizeMarginUnit`.
 */
export const marginUnitCopy = (raw: string | null | undefined) => {
  const unit = marginUnitOf(raw);
  return unit ? MARGIN_UNIT_COPY[unit] : null;
};
